import { ScreenContext } from '@/contexts/ScreenContext';
import { GameType as GameTypeEnum } from '@/models/games/enums/GameType';
import { GiftItem } from '@/modules/giftPanel/GiftItem';
import { Gift } from '@/modules/report/slice/giftRecord';
import { RwdSvgIcon } from '@/modules/rwdGame/components/RwdSvgIcon/RwdSvgIcon';
import BScroll from '@better-scroll/core';
import { BScrollConstructor } from '@better-scroll/core/dist/types/BScroll';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { AmountText } from '../../../../utils/formatter';
import i18n from '../../../locales/i18n';
import { getMainUserState } from '../../../main/selector';
import { DetailListDivider } from '../ReportDivider';
import './index.scss';

type DetailData = {
    title: string;
    content: JSX.Element;
    lang_direction?: boolean | undefined;
};

type DetailProps = {
    bet: Gift;
    gameName: string;
};

type DetailResultListItemProps = {
    titleId: string;
    children: JSX.Element;
    langDirection?: boolean | undefined;
    bet: Gift;
};
const DetailResultListItem = (props: DetailResultListItemProps) => {
    const { titleId, langDirection, children } = props;
    return (
        <>
            <DetailListItem titleId={titleId} setLangDirection={langDirection}>
                {children}
            </DetailListItem>
        </>
    );
};

export const GiftDetail = (props: DetailProps) => {
    const { IsCommonWallet } = useSelector((state: RootState) =>
        getMainUserState(state)
    );
    const { bet } = props;
    const { t } = useTranslation();
    const [data, setData] = useState<DetailData[]>([]);

    //scroller handling
    const { isMobile } = useContext(ScreenContext);
    const startedRef = useRef<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);
    const [scrollObj, setScrollObj] = useState<BScrollConstructor>();

    //initialization
    useEffect(() => {
        const data: DetailData[] = [];
        data.push({
            title: `${t('betlog.date')} / ${t('betlog.time')}`,
            content: <>{`${bet.Date} / ${bet.Time}`}</>,
        });
        data.push({
            title: `${t('betlog.game')} / ${t('betlog.round')}`,
            content: <RoundText {...props} />,
        });
        data.push({
            title: `${t('tableinfo.dealer')}`,
            content: <>{bet.DealerName}</>,
        });
        data.push({
            title: t('gift_transaction_id'),
            content: <>{bet.ID}</>,
        });
        if (!IsCommonWallet) {
            data.push({
                title: t('betlog.start_balance'),
                content: IsCommonWallet ? (
                    <></>
                ) : (
                    <>
                        <AmountText
                            amount={(bet.Balance + bet.GiftAmount) / 100}
                            with_color={false}
                            disableAuto={true}
                            // fontSize={REPORT_SIZE.TEXT}
                            className="detail-list-item-text-font"
                        />
                    </>
                ),
                lang_direction: false,
            });
        }
        data.push({
            title: t('gift_amount'),
            content: (
                <AmountText
                    amount={bet.GiftAmount / 100}
                    with_color={false}
                    disableAuto={true}
                    // fontSize={REPORT_SIZE.TEXT}
                    className="detail-list-item-text-font"
                />
            ),
            lang_direction: false,
        });
        if (!IsCommonWallet) {
            data.push({
                title: t('betlog.balance'),
                content: IsCommonWallet ? (
                    <></>
                ) : (
                    <AmountText
                        amount={bet.Balance / 100}
                        with_color={false}
                        disableAuto={true}
                        // fontSize={REPORT_SIZE.TEXT}
                        className="detail-list-item-text-font"
                    />
                ),
                lang_direction: false,
            });
        }
        data.push({
            title: `${t('gift_type')}`,
            content: (
                <Box>
                    <RwdSvgIcon portraitIconSize={80} landscapeIconSize={120}>
                        <GiftItem info={{ ID: bet.GiftType }} />
                    </RwdSvgIcon>
                </Box>
            ),
        });
        setData(data);
        console.log('debug::bet::record', bet, IsCommonWallet, props);
    }, [bet, IsCommonWallet, props]);

    useEffect(() => {
        if (isMobile) {
            return;
        }
        if (ref && ref.current && !startedRef.current) {
            startedRef.current = true;
            const bs = BScroll(ref.current as HTMLDivElement, {
                scrollX: false,
                scrollY: true,
                click: true,
                bounce: false,
                momentum: true,
                mouseWheel: true,
                probeType: 3,
            });
            setScrollObj(bs);
        }
        if (scrollObj && ref && ref.current) {
            setTimeout(() => {
                scrollObj.refresh();
            }, 200);
        }
    }, [ref.current]);

    return (
        <>
            <Box
                className="gift-record-detail"
                overflow={'auto'}
                ref={ref}
                sx={{
                    '&::-webkit-scrollbar': {
                        display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
                    },
                    '&-ms-overflow-style': {
                        display: 'none', // Hide the scrollbar for IE
                    },
                }}
            >
                <List className="detail-list">
                    {data.map((item, idx) => (
                        <div key={`item${idx}`}>
                            {
                                <DetailResultListItem
                                    titleId={item.title}
                                    langDirection={item.lang_direction}
                                    bet={bet}
                                >
                                    {item.content}
                                </DetailResultListItem>
                            }
                            {idx < data.length - 1 && <DetailListDivider />}
                        </div>
                    ))}
                </List>
            </Box>
        </>
    );
};
type DetailListItemProps = {
    titleId: string;
    onClick?: () => void;
    iconButton?: JSX.Element;
    children: JSX.Element;
    setLangDirection?: boolean;
};
const DetailListItem = (props: DetailListItemProps) => {
    const {
        titleId,
        onClick,
        iconButton,
        children,
        setLangDirection = true,
    } = props;
    return (
        <ListItem className="detail-list-item" onClick={onClick}>
            <ListItemText>
                <Stack direction={'row'} className="detail-list-item-text-root">
                    <Stack
                        direction={'row'}
                        className="detail-list-item-text-container"
                    >
                        <Typography
                            sx={{
                                color: '#BF9A5D',
                                // fontSize: REPORT_SIZE.TEXT,
                            }}
                            className="detail-list-item-text-font"
                        >
                            {titleId}
                        </Typography>
                        {iconButton}
                    </Stack>
                    <Box
                        sx={{
                            // fontSize: REPORT_SIZE.TEXT,
                            direction: `${setLangDirection ? i18n.dir(i18n.language) : ''}`,
                            textAlign: 'right',
                        }}
                        className="detail-list-item-text-font"
                    >
                        {children}
                    </Box>
                </Stack>
            </ListItemText>
        </ListItem>
    );
};

export const RoundText = ({ bet, gameName }: DetailProps) => {
    const { i18n } = useTranslation();
    const [languageDir, setLanguageDir] = useState<string>('ltr');
    useEffect(() => {
        setLanguageDir(i18n.dir(i18n.language));
    }, [i18n.language]);
    switch (bet.GameType) {
        case GameTypeEnum.SeDie:
        case GameTypeEnum.TeenPatti2020:
        case GameTypeEnum.AndarBahar:
        case GameTypeEnum.Roulette:
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.SicBo:
        case GameTypeEnum.ThaiHiLo:
        case GameTypeEnum.FishPrawnCrab:
        case GameTypeEnum.CarnivalTreasure:
            return (
                <>
                    {languageDir == 'rtl'
                        ? `${bet.Round == 0 ? '-' : bet.Round} / ${gameName}`
                        : `${gameName} / ${bet.Round == 0 ? '-' : bet.Round}`}
                </>
            );
        default:
            return (
                <>
                    {languageDir == 'rtl'
                        ? `${bet.Round == 0 ? '-' : `${bet.Shoes}-${bet.Round}`} / ${gameName}`
                        : `${gameName} / ${bet.Round == 0 ? '-' : `${bet.Shoes}-${bet.Round}`}`}
                </>
            );
    }
};
